// The Comparison table's row model, kept apart from its rendering so it can be
// tested directly. This logic previously sat inline in BillComparisonTable.tsx,
// which is why the Job 54 mapping defect shipped without a test — the pairing
// could only be reached by clicking through the UI.
import type { CalcExclusion, ShadowBillSection } from '../types/billTypes';
import { itemKey, resolveChargeMap, type ChargeMapRow } from './chargeMap';
import { canonicalChargeName } from './chargeNames';
import { canonicalSectionFor } from './chargeSectionRegistry';
import type { NameAliases } from './nameAliases';

export const norm = (v: string) => v.trim().toLowerCase();

/** The key two sides must agree on to land on one row.
 *
 *  Folds hyphens, underscores and repeated spaces on top of the canonical
 *  name, because the two sides legitimately spell the same charge differently:
 *  the calculation emits `Off-Peak` (RETAIL_ENERGY_NAMES.offPeak) while the
 *  charge map targets the vocabulary's `Off Peak`. Without the fold they key
 *  differently and one charge renders as two rows — expected-only and
 *  invoice-only — with the variance wrong on both.
 *
 *  Deliberately NOT `norm`: that is used for alias matching and exclusion
 *  comparisons, which compare against stored text and must keep matching it
 *  literally. This fold is only ever a row key, never displayed — `label`
 *  stays the human-readable name.
 *
 *  Verified safe against the dev database: folding collides no two distinct
 *  names, either in bv_expected_charge_item (75 rows) or among the
 *  descriptions the expected side actually emits. */
const pairKey = (description: string): string =>
  canonicalChargeName(description).trim().toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    // The expected-item vocabulary spells it "Supplementery Metering" and that
    // spelling is kept (charge map targets point at it); the calculation emits
    // "Supplementary Metering ($/meter/year)". One charge, so one key.
    .replace(/\bsupplementery\b/g, 'supplementary');

/**
 * The pair key with a trailing parenthetical removed.
 *
 * The expected vocabulary spells the charging unit into the name, while the
 * charge map's `expected_item` is written bare. Keyed strictly the two never
 * meet, and a correctly mapped charge still reports unpaired.
 *
 * Nothing about the parenthetical's CONTENT is interpreted — no unit list, no
 * vocabulary — so this holds for whatever a sheet or a retailer puts in
 * brackets.
 *
 * A trailing demand unit written as a suffix is dropped too: the catalogue
 * names buckets "Low Season Peak Demand - kW" while a map row targets the bare
 * "Low Season Peak Demand" (AGL "Winter Demand", NMI 63050013873). pairKey has
 * already turned the hyphen into a space, so the suffix arrives as " kw"/" kva".
 *
 * Deliberately not folded into `pairKey` itself: the annotation distinguishes
 * real charges. Two names differing only inside the brackets can be different
 * charges at different rates, and collapsing them would let one answer for the
 * other. Consulted only as a FALLBACK, and only when it lands on exactly one
 * expected row — see the map branch in `pairLines`.
 */
const unitlessKey = (description: string): string =>
  pairKey(description).replace(/\s*\([^)]*\)\s*$/, '').replace(/\s+k(w|va)$/, '').trim();

/**
 * The key an EXPECTED line pairs on, in the section it was grouped under.
 *
 * Job 67, network side: some distributors' tariffs label the network standing
 * charge a bare `Supply` (Ausgrid EA256), while the charge map targets the
 * vocabulary's `Network Supply`. Under Distribution those are one charge, so the
 * expected `Supply` keys as `network supply` there and a mapped "Network Access
 * Charge" lands on it. Under Metering, `Supply` is the retail supply charge and
 * keeps its own key. Only the key moves: the row keeps the calculation's label,
 * which is what saved exclusions are written against.
 */
const NETWORK_SUPPLY_ALIASES = new Set(['supply', 'supply charge']);
const shadowKey = (canonical: string, section: ShadowBillSection): string => {
  const key = pairKey(canonical);
  return section === 'distribution' && NETWORK_SUPPLY_ALIASES.has(key) ? 'network supply' : key;
};

export type ComparableLine = {
  description: string;
  amount: number;
  /** QTY — how many of the thing the rate is charged per. */
  volume?: number | null;
  volume_unit?: string | null;
  /** USAGE — what the charge ran over: kWh, or days. Independent of `volume`;
   *  metering and demand carry both. */
  usage?: number | null;
  usage_unit?: string | null;
  rate?: number | null;
  /** Job 20: the unit the rate is quoted in, stated by whichever side produced
   *  the line. Null on retailer lines until someone records it. */
  rate_unit?: string | null;
  dlf?: number | null;
  mlf?: number | null;
  /** Position of this line in the owning result's `lines[]`.
   *
   *  Set by the caller before the lines are grouped by section, because grouping
   *  destroys the array position and an edit has to know which line it writes
   *  back to. Optional: a caller that only renders (the report, the shadow bill
   *  document) has no need for it, and its absence simply makes the row
   *  read-only rather than mis-targeting a write. */
  sourceIndex?: number;
};

export type SideLine = {
  description: string;
  amount: number;
  volume: number | null;
  volumeUnit: string | null;
  usage: number | null;
  usageUnit: string | null;
  rate: number | null;
  rateUnit: string | null;
  /** True when the unit was worked out from the line rather than recorded on it.
   *  Surfaced so the UI can say so instead of presenting it as a stored fact. */
  rateUnitDerived: boolean;
  /** dlf × mlf, when the line carries both. Retailer lines never do. */
  lossFactor: number | null;
  /** See ComparableLine.sourceIndex. Null when the caller did not supply one. */
  sourceIndex: number | null;
};

// A charge after pairing expected + retailer lines by canonical description.
//
// Both sides keep their constituent lines rather than being summed on the way in.
// Collapsing them early is what previously forced the rate to be discarded as
// soon as two lines shared a description, so a multi-line charge showed a blank
// rate. Aggregation happens at render time in `band()`, which can report the
// line count instead of silently dropping the figure.
export type ChargeRow = {
  key: string;
  label: string;
  section: ShadowBillSection;
  shadow: SideLine[];
  actual: SideLine[];
  /** Set on a retailer-only row, so a mapping can be keyed off the invoice's own wording. */
  raw?: string;
  /** Invoice descriptions folded in through a saved alias — the provenance shown
   *  next to an Unmap control so a mis-mapping can be undone. */
  mappedRaws?: string[];
  /** Job 59: the section this charge was calculated into, when an override moved
   *  it somewhere else. Absent unless the row was actually moved. */
  movedFrom?: ShadowBillSection;
  /** The retailer line matched no mapping row. Rendered under its own section
   *  with a marker and EXCLUDED from the variance total, so an unrecognised
   *  charge cannot silently inflate or hide a shortfall. Switchable out through
   *  bv_bill.calc_exclusions like any other row. */
  unmapped?: boolean;
  /** The retailer line is not in the charge map but its name is IDENTICAL to
   *  this expected charge (after the usual separator fold), so it paired on the
   *  name. Counted like any paired row; shown so the pairing is visible. */
  nameMatched?: boolean;
  /** VALIDATION POSSIBLE = N — the retailer's figure is the source of truth and
   *  no expected line exists to compare against. 121 rows, every environmental
   *  certificate type among them. Not a shortfall. */
  passthrough?: boolean;
};

/**
 * One rendered row of the charge breakdown.
 *
 * QTY and USAGE are separate and independent, because for metering and demand
 * both are needed to check the charge: 1 meter (qty) across 31 days (usage), or
 * 82.987 kVA (qty) across 31 days (usage). A consumption charge fills usage
 * alone; there is nothing to count.
 *
 * `usage` is the metered figure as billed. The loss factor is reported beside it
 * in its own field rather than folded into it.
 */
export type Band = {
  rate: number | null;
  rateUnit: string | null;
  /** See SideLine.rateUnitDerived. */
  rateUnitDerived: boolean;
  qty: number | null;
  qtyUnit: string | null;
  usage: number | null;
  usageUnit: string | null;
  lossFactor: number | null;
  total: number;
  lineCount: number;
  /** The line this row's figures came from, when exactly one did — the only case
   *  where a number typed into the row can be written back unambiguously. Null
   *  for an aggregated row: three lines summed to one quantity cannot be
   *  un-summed, so those rows are edited in the full editor instead. */
  sourceIndex: number | null;
};

// Units that name a COUNT of things — the QTY column. Everything else a line
// can be measured in (kWh, MWh, days) is something it ran over: the Usage column.
const COUNT_UNITS = /^(meters?|servicepoints?|mtr|kva|kw)$/i;

/**
 * Where a line's single quantity belongs when it only carries one.
 *
 * Every invoice parsed before QTY and Usage were separated — and every shadow
 * result calculated before it — stores one figure in `volume`, whatever it
 * measures. The retailer parser still does. Without this, a stored invoice's
 * 5,420 kWh would sit under QTY, against an expected side that now puts kWh
 * under Usage, and every row would read as a mismatch.
 *
 * Decided from the unit, which is the only evidence the line carries.
 */
export function splitLegacyQuantity(l: ComparableLine): {
  volume: number | null; volumeUnit: string | null;
  usage: number | null; usageUnit: string | null;
} {
  const volume = l.volume ?? null;
  const volumeUnit = l.volume_unit ?? null;
  const usage = l.usage ?? null;
  const usageUnit = l.usage_unit ?? null;

  // The line already distinguishes them, or has nothing to distinguish.
  if (usage != null || volume == null) return { volume, volumeUnit, usage, usageUnit };

  // A count stays a count; anything else is what the charge ran over.
  if (volumeUnit && COUNT_UNITS.test(volumeUnit.trim())) {
    return { volume, volumeUnit, usage: null, usageUnit: null };
  }
  return { volume: null, volumeUnit: null, usage: volume, usageUnit: volumeUnit };
}

/**
 * The rate unit for a line that does not record one.
 *
 * `rate_unit` is written into the stored result at calculation time (Job 20), so
 * every bill calculated before that — and every retailer line, since the parser
 * does not extract it — has a bare number where the unit should be. Erin's
 * report was precisely that: "expected metering is $/meter/annum, billed is
 * $/meter/day", two figures that mean different things and looked identical.
 *
 * This does NOT guess. It works the unit out two ways, both checkable:
 *
 *  1. The charge name states it. Canonical names carry the unit in brackets —
 *     `Metering ($/meter/year)`, `Supply ($/servicePoint/day)`. Reading it back
 *     out is not inference, it is the same string the calculation wrote.
 *  2. Reconciliation. A rate is either the cents form (amount = qty × rate/100)
 *     or the dollars form (amount = qty × rate). Whichever reproduces the STORED
 *     amount is the form this line was actually billed in. If neither does — a
 *     prorated annual charge, say — nothing is claimed.
 *
 * Returns null when it cannot be established, and the caller keeps printing the
 * bare number. A unit shown here is one the line's own figures support.
 */
export function deriveRateUnit(
  l: ComparableLine, quantity: number | null, quantityUnit: string | null,
): string | null {
  // 1. Stated in the charge name.
  const named = /\(([^)]*\/[^)]*)\)/.exec(l.description ?? '');
  if (named) return named[1].trim();

  // 2. Reconciled against the amount.
  const rate = l.rate;
  if (rate == null || rate === 0 || quantity == null || !quantityUnit) return null;
  const amount = l.amount;
  if (!Number.isFinite(amount)) return null;

  // Stored amounts are rounded to the cent, so exact equality is not the bar.
  const tolerance = Math.max(0.02, Math.abs(amount) * 0.005);
  const centsForm = Math.abs(amount) - Math.abs(quantity * rate / 100);
  const dollarForm = Math.abs(amount) - Math.abs(quantity * rate);

  // A rate is quoted per ONE of the thing: $/day and $/meter, from quantities
  // counted in days and meters. `kWh` is not a plural and is left alone.
  const per = /s$/.test(quantityUnit) ? quantityUnit.replace(/s$/, '') : quantityUnit;

  if (Math.abs(centsForm) <= tolerance && Math.abs(dollarForm) > tolerance) {
    return `¢/${per}`;
  }
  if (Math.abs(dollarForm) <= tolerance && Math.abs(centsForm) > tolerance) {
    return `$/${per}`;
  }
  // Both reconcile (a rate of 0, or a quantity that makes them equal), or
  // neither does. Either way there is no single answer to give.
  return null;
}

export const toSide = (l: ComparableLine): SideLine => {
  const split = splitLegacyQuantity(l);
  // The quantity the rate is applied to: usage for a consumption or daily
  // charge, the count for a per-meter or demand charge.
  const applied = split.usage ?? split.volume;
  const appliedUnit = split.usage != null ? split.usageUnit : split.volumeUnit;
  const derived = l.rate_unit ? null : deriveRateUnit(l, applied, appliedUnit);

  return {
    description: l.description,
    amount: l.amount,
    ...split,
    rate: l.rate ?? null,
    rateUnit: l.rate_unit ?? derived,
    rateUnitDerived: !l.rate_unit && derived != null,
    lossFactor: l.dlf != null && l.mlf != null ? l.dlf * l.mlf : null,
    sourceIndex: l.sourceIndex ?? null,
  };
};

export function band(lines: SideLine[]): Band | null {
  if (lines.length === 0) return null;
  const total = lines.reduce((s, l) => s + l.amount, 0);

  if (lines.length === 1) {
    const l = lines[0];
    // The shadow calculation bills usage × rate × dlf × mlf, so the loss-adjusted
    // figure is what the rate is actually applied to. Showing both makes the
    // loss factor's effect checkable rather than implicit.
    return {
      rate: l.rate,
      rateUnit: l.rateUnit,
      rateUnitDerived: l.rateUnitDerived,
      qty: l.volume,
      qtyUnit: l.volumeUnit,
      usage: l.usage,
      usageUnit: l.usageUnit,
      lossFactor: l.lossFactor,
      total,
      lineCount: 1,
      sourceIndex: l.sourceIndex,
    };
  }

  // Several lines share one description. The amounts sum correctly, but no single
  // rate represents all of them — reported as absent with the line count so the
  // reader knows why, instead of showing one line's rate as if it were the charge's.
  const allHaveVolume = lines.every((l) => l.volume != null);
  const allHaveUsage = lines.every((l) => l.usage != null);
  // One unit only counts as the charge's unit when every line agrees on it.
  const units = new Set(lines.map((l) => l.rateUnit).filter(Boolean));
  return {
    rate: null,
    rateUnit: units.size === 1 ? [...units][0]! : null,
    // Only claim the unit is recorded when every line actually recorded it.
    rateUnitDerived: lines.some((l) => l.rateUnitDerived),
    qty: allHaveVolume ? lines.reduce((s, l) => s + (l.volume ?? 0), 0) : null,
    qtyUnit: lines.find((l) => l.volumeUnit)?.volumeUnit ?? null,
    usage: allHaveUsage ? lines.reduce((s, l) => s + (l.usage ?? 0), 0) : null,
    usageUnit: lines.find((l) => l.usageUnit)?.usageUnit ?? null,
    lossFactor: null,
    total,
    lineCount: lines.length,
    // Deliberately null even though every constituent line has an index: the
    // quantity shown is a sum, and writing a typed figure back to one of the
    // lines behind it would silently restructure the charge.
    sourceIndex: null,
  };
}

/** The charge map plus the site facts its resolution order needs. */
export type MapContext = {
  rows: ChargeMapRow[];
  distributor: string | null;
  tariff: string | null;
  /**
   * Approved distributor names and their other spellings (migration 138), so a
   * map row written for "Ergon" applies to a site saying "Ergon Energy".
   */
  distributorAliases?: NameAliases;
  /**
   * Charge names (itemKey-folded) this bill prints in more than one section.
   * Each such line resolves only within its own section; see
   * itemsInSeveralSections.
   */
  strictItems?: ReadonlySet<string>;
};

/**
 * The charge names a bill prints under more than one section heading.
 *
 * The resolver falls back to a row written for another section when this
 * section maps nothing, which is right when the parser filed a line under the
 * wrong heading. It is wrong when the invoice itself prints the name twice.
 * SmartestEnergy prints "Shoulder Energy" under Retail Energy and under Network
 * Charges (NMI 40010462152). With only network rows in the map, the retail line
 * moved into Distribution and was added onto network Shoulder Usage.
 */
export function itemsInSeveralSections(
  lines: ReadonlyArray<{ description: string; section: string }>,
): Set<string> {
  const seen = new Map<string, string>();
  const twice = new Set<string>();
  for (const l of lines) {
    const k = itemKey(l.description);
    const s = seen.get(k);
    if (s === undefined) seen.set(k, l.section);
    else if (s !== l.section) twice.add(k);
  }
  return twice;
}

const strictFor = (mapContext: MapContext, description: string) =>
  !!mapContext.strictItems?.has(itemKey(description));

/**
 * Which section an invoice line should be COMPARED in.
 *
 * `pairLines` runs once per section, so a retailer line can only ever meet an
 * expected line bucketed alongside it. Retailers routinely print a charge under
 * a different heading from the one we calculate it under — and the same retailer
 * may file it differently from one bill to the next. Bucketed by the retailer's
 * own section those never meet, and the comparison shows one charge twice:
 * "Invoice only" against "Expected only".
 *
 * Doing this BEFORE pairing rather than reconciling afterwards is also what
 * keeps the match unambiguous. A bill can calculate two charges sharing a base
 * name in different sections — a retail one and a network one — and merging on
 * name after both had been pulled into the same section could answer one with
 * the other's figure.
 *
 * Falls back to the line's own section whenever the map has nothing to say,
 * which is every retailer with no map rows yet.
 */
export function actualSectionFor(
  description: string,
  ownSection: ShadowBillSection,
  mapContext?: MapContext,
): ShadowBillSection {
  if (!mapContext?.rows.length) return ownSection;
  const hit = resolveChargeMap(mapContext.rows, {
    distributor: mapContext.distributor,
    tariff: mapContext.tariff,
    billSection: ownSection,
    billItem: description,
    strictSection: strictFor(mapContext, description),
    distributorAliases: mapContext.distributorAliases,
  });
  return hit?.expected_section ?? ownSection;
}

export function pairLines(
  shadowLines: ComparableLine[],
  actualLines: ComparableLine[],
  section: ShadowBillSection = 'other',
  // The charge map, plus the site facts the resolution order needs. One object
  // rather than separate parameters: supplying the rows while forgetting the
  // distributor would silently disable tiers 1-3 of the resolution and leave
  // every distributor-specific charge unmapped.
  //
  // Optional, so every existing caller and test is unaffected. Where supplied,
  // the map decides the pairing instead of canonical-name equality.
  mapContext?: MapContext,
): ChargeRow[] {
  // An empty rows array counts as "no map": a retailer whose mapping sheet has
  // not been loaded still pairs on canonical name, rather than reporting every
  // charge on its invoice as unrecognised.
  //
  // What a no-map retailer no longer gets is RENAMING. The retailer.extn
  // charge_aliases lookup that used to turn "NETWORK USAGE CHG" into "Network
  // Anytime" went with the comparison table's Map/Re-map picker: renames are
  // authored in bv_retailer_charge_map, which is the only mapping source now.
  // So a retailer absent from the map pairs only what already agrees by name,
  // and anything else lands unpaired until its map rows are loaded.
  const hasMap = !!mapContext?.rows.length;

  // The map keys a retailer line by its OWN wording; the row it lands on is
  // keyed by the expected name the map targets. That is what lets "Capacity
  // Charge" and "Anytime Demand" pair despite sharing no words.
  //
  // `section` is the canonical section these lines were grouped under by the
  // caller, which is exactly what the resolver scopes its first pass to.
  // The charges this bill expects in this section, keyed the way the map's
  // target is matched below. Lets the resolver choose between rows that share a
  // wording but mean different charges on different tariffs.
  const expectedHere = new Set(shadowLines.map((l) => unitlessKey(canonicalChargeName(l.description))));
  const isExpected = (target: string) => expectedHere.has(unitlessKey(canonicalChargeName(target)));

  const mapFor = (rawDesc: string): ChargeMapRow | null => {
    if (!mapContext || !hasMap) return null;
    return resolveChargeMap(mapContext.rows, {
      distributor: mapContext.distributor,
      tariff: mapContext.tariff,
      billSection: section,
      billItem: rawDesc,
      strictSection: strictFor(mapContext, rawDesc),
      isExpected,
      distributorAliases: mapContext.distributorAliases,
    });
  };

  const rows = new Map<string, ChargeRow>();
  const ensure = (key: string, label: string): ChargeRow => {
    let row = rows.get(key);
    if (!row) {
      row = { key, label, section, shadow: [], actual: [] };
      rows.set(key, row);
    }
    return row;
  };

  // Both sides route their description through canonicalChargeName before keying,
  // so a bill calculated before the Job-39 rename pairs against one calculated
  // after, and the row label is always the canonical Master Data name.
  for (const l of shadowLines) {
    const canonical = canonicalChargeName(l.description);
    ensure(shadowKey(canonical, section), canonical).shadow.push(toSide(l));
  }
  for (const l of actualLines) {
    const hit = mapFor(l.description);
    if (hit) {
      // A group's members all carry the same expected_item, so they land on one
      // row and sum there — the section-level reconciliation a collapsed bill
      // needs, since item-level tariff matching cannot hold across a group.
      //
      // The target is canonicalised the same way the expected side is, so a map
      // row written against a pre-Job-39 name still lands on the same key as the
      // shadow line it is meant to pair with. A row with no target keeps the
      // retailer's own wording: it is mapped (the charge is recognised) but has
      // nothing on our side to be renamed to.
      const label = canonicalChargeName(hit.expected_item ?? l.description);
      // Exact first. Only if the map's bare name matches no expected row do we
      // retry ignoring the parenthetical, and only when that is unambiguous: one
      // candidate means the fold is certain, two means the bill carries names
      // that differ only inside the brackets, and guessing between them would
      // put the retailer's figure against the wrong rate. Restricted to rows
      // with an expected side — folding onto another unmapped retailer line
      // would merge two invoice charges, not pair one.
      let key = shadowKey(label, section);
      let rowLabel = label;
      if (!rows.has(key)) {
        const want = unitlessKey(label);
        const candidates = [...rows.values()].filter(
          (r) => r.shadow.length > 0 && unitlessKey(r.label) === want,
        );
        if (candidates.length === 1) {
          key = candidates[0].key;
          rowLabel = candidates[0].label;
        }
      }
      const row = ensure(key, rowLabel);
      // Pass-through ONLY when the retailer charge map says so (map_state
      // 'passthrough', VALIDATION POSSIBLE = N): the retailer's figure is the
      // source of truth, so the line is left out of the variance and added back
      // at the invoice's amount. Decided 2026-10-07: nothing else is pass-through.
      // 'unvalidated' ("nobody has decided yet") counts in the totals like an
      // unmapped charge, so it is seen rather than silently added back.
      //
      // Only when there is nothing on our side to compare against. The shadow
      // loop above has already run, so an empty shadow side here is final. If we
      // DID calculate an expected figure for this charge, both numbers are real
      // and the comparison is meaningful.
      if (row.shadow.length === 0 && hit.map_state === 'passthrough') {
        row.passthrough = true;
      }
      row.actual.push(toSide(l));
      // Provenance: which invoice lines folded onto this row. For a collapsed
      // group that is the only way to see the four charges behind one figure.
      (row.mappedRaws ??= []).push(l.description);
      // Where the map says this charge belongs, which is not always the section
      // the retailer printed it under. This replaces the per-retailer section
      // override the comparison table used to write
      // (`retailer.extn.section_overrides`, the "Move to…" dropdown): the map
      // row carries `expected_section`, so the sheet decides placement and
      // nobody has to correct it bill by bill.
      //
      // `movedFrom` is set exactly as the override used to set it, so the row
      // still reports where it came from, and `applyCanonicalSections` still
      // runs afterwards and still wins.
      if (hit.expected_section && hit.expected_section !== row.section) {
        row.movedFrom = row.section;
        row.section = hit.expected_section;
      }
      continue;
    }
    if (hasMap) {
      // Not in the map, but its name is IDENTICAL to an expected charge in this
      // section that no retailer line answers yet: pair it. Decision 2026-09-29
      // (NMI 41020292768 showed Peak Demand $1,224.09 twice, once each side).
      // Identity, not similarity: "Peak Demand Charge" still stays unmapped
      // below, which is the job 71 guard. And never onto a row a mapped line
      // already answers, where a same-named line is a second charge.
      const same = rows.get(shadowKey(canonicalChargeName(l.description), section));
      if (same && same.shadow.length > 0 && same.actual.length === 0) {
        same.actual.push(toSide(l));
        same.nameMatched = true;
        continue;
      }
      // A map was supplied and this line is not in it. Report that, do not fall
      // back to name matching — guessing a similar name is the defect (job 71)
      // this mapping exists to remove. `raw` carries the invoice's own wording
      // so the row can say what was not recognised.
      //
      // The key is NAMESPACED, and must stay that way. Keyed on the description
      // alone, an unmapped line whose wording happens to equal an expected
      // label lands on that expected row and stamps `unmapped` on the whole
      // thing — both sides then leave the variance, so a real shortfall
      // disappears along with the expected amount it was measured against. That
      // collision is itself a name match, which is the fallback this feature
      // exists to remove. Nothing downstream keys off `key` for persistence:
      // exclusions and section overrides are written against `label`, and the
      // invoice's own wording is on `raw`.
      const row = ensure(`unmapped:${pairKey(l.description)}`, l.description);
      row.unmapped = true;
      row.raw = l.description;
      row.actual.push(toSide(l));
      continue;
    }
    // No map for this retailer — pair on the canonical name alone.
    //
    // This is the BASE comparison, not a mapping: it is what lets an expected
    // "Peak" meet a retailer "Peak". The retailer.extn charge_aliases lookup
    // that used to sit here was removed with the Map/Re-map picker — a renamed
    // charge is expressed in bv_retailer_charge_map now, so this path no longer
    // renames anything, it only pairs what already agrees.
    const target = canonicalChargeName(l.description);
    const row = ensure(shadowKey(target, section), target);
    row.actual.push(toSide(l));
    if (row.shadow.length === 0) row.raw = l.description; // nothing expected to pair with
  }
  return [...rows.values()];
}


/**
 * How many separate invoice lines a row is standing in for.
 *
 * Rows are keyed by canonical charge name, so several invoice lines the charge map points at
 * one expected item merge into a single row. Shown next to the row so one figure standing for
 * four charges reads as the merge it is, rather than as the table losing lines.
 */
export function invoiceLineCount(row: ChargeRow): number {
  return row.actual.length;
}

/**
 * Charges that belong to one section regardless of where either side filed them.
 * Supply is the case: always metering. Run AFTER `pairLines` has applied the
 * charge map's `expected_section` so the rule is unconditional — "always" has to
 * beat the map, or the grouping is only right for the retailers whose sheet
 * someone remembered to fill in.
 *
 * This has to be a pass over PAIRED rows, not over the incoming lines. A row's
 * `label` is the canonical name, which is what a saved charge alias resolves an
 * invoice's own wording to — an invoice printing "Service Charge" mapped to
 * "Supply Charge" only becomes recognisable as supply here, after `pairLines`
 * has applied the alias. Reclassifying raw invoice lines upstream (which
 * `invoiceParserService.reclassifySupplyLines` does) cannot see that rename, so
 * the aliased case landed in Retail Energy Charges and this is what fixes it.
 *
 * `movedFrom` is set the same way an override sets it, so the row still reports
 * where it was calculated.
 */
export function applyCanonicalSections(rows: ChargeRow[]): ChargeRow[] {
  return rows.map((r) => {
    // The row's current section is passed as context: job 67 leaves a supply
    // charge the invoice filed under Network where it is.
    const home = canonicalSectionFor(r.label, r.section);
    if (!home || home === r.section) return r;
    return { ...r, section: home, movedFrom: r.movedFrom ?? r.section };
  });
}

// The rule itself lives in chargeSectionRegistry — the one definition shared by
// the shadow calculation, the parser's billed-line pass and this row pass.
// Re-exported so existing importers of it from this module keep working.
export { canonicalSectionFor };

/**
 * Job 20: "Price & Units attributed to contract and billed values not present in
 * comparison". A bare 4,171 beside a bare 23.26 tells a validator nothing — the
 * first is $/meter/year and the second $/meter/day. The unit is half the figure.
 *
 * A rate with no recorded unit prints as the number alone rather than borrowing
 * the other side's unit, which would be a guess presented as fact.
 */
export function formatRate(b: Band): string {
  if (b.rate == null) return '—';
  const n = b.rate.toLocaleString('en-AU', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return b.rateUnit ? `${n} ${b.rateUnit}` : n;
}

/**
 * True when the two sides quote their rate in different units — the case Erin
 * reported: contracted $/meter/year against billed $/meter/day. Comparing those
 * numbers directly is meaningless, so the table flags it rather than showing a
 * variance that looks like a pricing error.
 *
 * Returns false whenever it cannot know: either side missing, or either unit
 * unrecorded. An unrecorded unit is not evidence of disagreement.
 */
export function rateUnitsDisagree(self: Band | null, other: Band | null): boolean {
  if (!self || !other) return false;
  if (!self.rateUnit || !other.rateUnit) return false;
  return norm(self.rateUnit) !== norm(other.rateUnit);
}

/**
 * The same guard for the QTY column (Job 60). Now that a metering line reports
 * meters rather than days, our `1 meter` can sit beside a retailer's `31 days`
 * for the same charge — a difference of 30 that is not a discrepancy, just two
 * ways of quoting the same thing.
 *
 * Singular and plural are the same unit: `1 meter` and `2 meters` agree.
 * Like rateUnitsDisagree, an unrecorded unit on either side returns false —
 * silence is not evidence of disagreement.
 */
export function qtyUnitsDisagree(self: Band | null, other: Band | null): boolean {
  return unitsDisagree(self?.qtyUnit, other?.qtyUnit);
}

/** The same guard for the Usage column — kWh against days is not a variance. */
export function usageUnitsDisagree(self: Band | null, other: Band | null): boolean {
  return unitsDisagree(self?.usageUnit, other?.usageUnit);
}

function unitsDisagree(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const singular = (u: string) => norm(u).replace(/s$/, '');
  return singular(a) !== singular(b);
}

const qtyNum = (v: number) => v.toLocaleString('en-AU', { maximumFractionDigits: 0 });

/**
 * QTY — how many of the thing the rate is charged per: `1 meter`, `2 meters`,
 * `82.987 kVA`.
 *
 * Job 20 briefly merged QTY and Usage into one column, on the report that the
 * two looked duplicated. They are not duplicated for the charges that matter:
 * metering is a meter count over a number of days, and demand is a kVA figure
 * over a number of days. Merging them dropped the second number for exactly
 * those rows, so the columns are separate again and each says which it is.
 *
 * An em dash, never a bare zero — a charge that counts nothing (consumption) is
 * not a charge for zero things.
 */
export function formatQty(b: Band): string {
  const { qty, qtyUnit } = b;
  if (qty == null) return '—';
  return qtyUnit ? `${qtyNum(qty)} ${qtyUnit}` : qtyNum(qty);
}

/**
 * USAGE — what the charge ran over: `5,420 kWh`, `31 days`.
 *
 * The metered figure only. A loss-adjusted second number used to be printed
 * beside it (`5,420 kWh → 5,845 kWh`), which made the column twice as wide and
 * put the emphasis on a derived figure rather than the billed one. The Loss
 * Factor sits in its own column on the same row, so the adjustment is still
 * visible without crowding the quantity.
 */
export function formatUsage(b: Band): string {
  const { usage, usageUnit } = b;
  if (usage == null) return '—';
  return usageUnit ? `${qtyNum(usage)} ${usageUnit}` : qtyNum(usage);
}

// ─── Exclusions and totals ────────────────────────────────────────────────────

const GST_RATE = 0.1;
const round2 = (n: number) => parseFloat(n.toFixed(2));

const SECTION_KEYS: ShadowBillSection[] = ['retail', 'distribution', 'metering', 'environmental', 'other'];

/** True when this row is switched out. A section-wide exclusion (`description:
 *  null`) drops every charge in that section; otherwise the charge name must
 *  match, ignoring case and surrounding whitespace. */
export function isRowExcluded(row: ChargeRow, exclusions: CalcExclusion[]): boolean {
  return exclusions.some(
    (e) => e.section === row.section && (e.description === null || norm(e.description) === norm(row.label)),
  );
}

export type ComparisonTotals = {
  sections: Record<ShadowBillSection, { expected: number; retailer: number }>;
  expectedSubtotal: number;
  retailerSubtotal: number;
  expectedGst: number;
  retailerGst: number | null;
  expectedTotal: number;
  retailerTotal: number | null;
  excluded: { count: number; expected: number; retailer: number };
};

/**
 * Job 53: totals derived from the rows rather than read off the stored result.
 *
 * An excluded charge leaves BOTH sides, so the variance ignores it — a charge
 * switched out while validating should not then be reported as a discrepancy.
 * That is also why these totals can no longer come from
 * `shadow_result.section_totals`: an exclusion, or a section override (Job 59),
 * changes which charge counts where, and a stored total cannot know that.
 *
 * Expected GST is recomputed at 10% of the remaining expected subtotal — an
 * exclusion must not leave GST covering charges that are no longer counted. The
 * retailer's GST is passed through untouched: we never recompute someone else's
 * invoice, we only report what they printed.
 *
 * A passthrough row leaves the variance too: the charge map says the invoice is
 * the source of truth for it, so it is added back at the invoice's amount and
 * compares to nothing. The row is still returned and rendered.
 *
 * An UNMAPPED row counts (decided 2026-10-07). It used to be left out like a
 * passthrough, so a network charge nobody had mapped vanished from the retailer
 * total and the bill read as matched. Now it sits on the retailer side with
 * nothing expected against it, and shows as variance until it is mapped.
 */
export function comparisonTotals(
  rows: ChargeRow[],
  exclusions: CalcExclusion[],
  retailerGst: number | null,
): ComparisonTotals {
  const sections = Object.fromEntries(
    SECTION_KEYS.map((k) => [k, { expected: 0, retailer: 0 }]),
  ) as ComparisonTotals['sections'];

  const excluded = { count: 0, expected: 0, retailer: 0 };
  const sum = (ls: SideLine[]) => ls.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  for (const r of rows) {
    const expected = sum(r.shadow);
    const retailer = sum(r.actual);
    if (isRowExcluded(r, exclusions)) {
      excluded.count += 1;
      excluded.expected = round2(excluded.expected + expected);
      excluded.retailer = round2(excluded.retailer + retailer);
      continue;
    }
    // Checked AFTER the exclusion test, so a row switched out by hand is still
    // reported as switched out. Not folded into `excluded`: that count is what
    // the validator chose to leave out, and a pass-through is the charge map's
    // decision, not theirs.
    if (r.passthrough) continue;
    const bucket = sections[r.section];
    if (!bucket) continue;
    bucket.expected = round2(bucket.expected + expected);
    bucket.retailer = round2(bucket.retailer + retailer);
  }

  const expectedSubtotal = round2(SECTION_KEYS.reduce((s, k) => s + sections[k].expected, 0));
  const retailerSubtotal = round2(SECTION_KEYS.reduce((s, k) => s + sections[k].retailer, 0));

  // GST is switchable like any other section. Switched off it leaves BOTH sides
  // and the comparison runs ex-GST, which is the only reason to switch it off:
  // a retailer that states GST on a different base is otherwise an unexplainable
  // variance sitting under charges that all reconcile.
  const gstOff = exclusions.some((e) => e.section === 'gst' && e.description === null);
  const expectedGst = gstOff ? 0 : round2(expectedSubtotal * GST_RATE);
  // null still means "no invoice parsed" — switching GST off must not turn an
  // absent retailer side into a zero one, which reads as "they charged nothing".
  const effectiveRetailerGst = retailerGst == null ? null : gstOff ? 0 : retailerGst;

  return {
    sections,
    expectedSubtotal,
    retailerSubtotal,
    expectedGst,
    retailerGst: effectiveRetailerGst,
    expectedTotal: round2(expectedSubtotal + expectedGst),
    retailerTotal: effectiveRetailerGst == null ? null : round2(retailerSubtotal + effectiveRetailerGst),
    excluded,
  };
}

/**
 * The money `comparisonTotals` dropped from the variance: the `passthrough`
 * rows (charge map: VALIDATION POSSIBLE = N), counted and summed on the
 * retailer side. This is the add-back. Unmapped rows are NOT here — they count.
 *
 * This exists so the table can STATE the gap. A passthrough row keeps its
 * amount on screen but contributes nothing to `retailerSubtotal`, so the
 * section total stops tying to the printed invoice and — until this figure is
 * shown — nothing explains the difference.
 *
 * The exclusion test runs FIRST, in exactly the order `comparisonTotals` uses
 * it. A charge switched out by hand is already reported through
 * `totals.excluded`, and counting it here as well would present the same
 * dollars to the reader twice. With that ordering the three figures reconcile
 * exactly, which is what the test asserts:
 *
 *   raw retailer total
 *     = retailerSubtotal + excluded.retailer + excludedFromVariance.retailer
 *
 * Expected has no counterpart figure on purpose: a passthrough row is only
 * flagged when its expected side is empty (see `pairLines`), so there is never
 * an expected amount to report here.
 */
export function excludedFromVariance(
  rows: ChargeRow[],
  exclusions: CalcExclusion[],
): { count: number; retailer: number } {
  let count = 0;
  let retailer = 0;
  for (const r of rows) {
    if (isRowExcluded(r, exclusions)) continue;
    if (!r.passthrough) continue;
    count += 1;
    retailer = round2(retailer + r.actual.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  }
  return { count, retailer };
}
